import { useGate } from '@hotjar/allowances';
import { useCurrentSiteId } from '@hotjar/site';
import { useTranslation } from '@hotjar/translations';
import type { SurveyWizardAccordionProps } from '../Accordion/SurveyWizardAccordion';

type TargetingSummaryProps = Pick<SurveyWizardAccordionProps, 'targeting'>;

// TODO: create a re-usable hooks to get modes from targeting rules
const getPageTargetingMode = (targeting: TargetingSummaryProps['targeting']) => {
  if (targeting === undefined) return undefined;
  const targetingRules = targeting?.filter(({ target }) => {
    return target.component === 'url' || target.component === 'trigger';
  });

  if (
    targetingRules.length === 1 &&
    targetingRules[0].target.match_operation === 'contains' &&
    targetingRules[0].target.pattern === '/'
  ) {
    return 'all';
  }

  if (targetingRules.length > 0) return 'specific';

  return undefined;
};

const getUserTargeting = (targeting: TargetingSummaryProps['targeting']) => {
  if (targeting === undefined) return [];
  return targeting.filter(({ target }) => {
    return target.component === 'attribute';
  });
};

const getUserTargetingMode = (userTargeting: TargetingSummaryProps['targeting'] = []) => {
  if (userTargeting.length === 0) {
    return 'all';
  }

  if (userTargeting.length > 0) return 'specific';

  return undefined;
};

export const TargetingSummary = ({ targeting }: TargetingSummaryProps): JSX.Element | null => {
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.targeting' });
  const currentSiteId = useCurrentSiteId();
  const canUseUserAttributes = useGate('feedback.user_attributes', {
    id: currentSiteId,
    type: 'site',
  });
  if (targeting === undefined) return null;
  const devicesTargeting = targeting.filter(({ target }) => target.component === 'device');
  const pageTargetingMode = getPageTargetingMode(targeting);
  const pagesTargeting = targeting.filter(
    ({ target }) => target.component === 'url' || target.component === 'trigger',
  );
  const userTargeting = getUserTargeting(targeting);
  const userTargetingMode = getUserTargetingMode(userTargeting);

  return (
    <span>
      {devicesTargeting.length === 3
        ? t('all-devices')
        : t('devices-number', { count: devicesTargeting.length })}
      {', '}
      {pageTargetingMode === 'all'
        ? t('all-pages')
        : t('page-targeting-rules', { count: pagesTargeting.length })}
      {canUseUserAttributes === false
        ? '.'
        : `, ${
            userTargetingMode === 'all'
              ? t('all-users')
              : t('users-number', { count: userTargeting.length })
          }.`}
    </span>
  );
};
